import {useMutation, useQuery, useQueryClient} from 'react-query';
import {
  checkEligibility,
  createCommande,
  downloadEligibiliteMasseResult,
  geocodeAdresse,
  getAdresseSuggestions,
  getAdressesBySiret,
  getContacts,
  getEligibiliteMasseSubmissions,
  getPortesCollecte,
  reverseGeocodeAdresse,
  getSearchHistory,
  getApsRequests,
  getApsRequestsFilters,
  getSearchHistoryFilters,
  sendDivopsAssistance,
  submitApsRequest,
  uploadEligibiliteMasseFile,
} from '../Api/api';
import {
  ApsRequestPayload,
  CommandePayload,
  DivopsApsHistoryQueryParams,
  DivopsAssistancePayload,
  DivopsHistoryQueryParams,
  EligibiliteMasseSubmission,
  EligibilityFormValues,
} from '../Types/types';

export const DIVOPS_HISTORY_QUERY_KEY = 'divops-search-history';

export const useGetAdresseSuggestions = (query: string) =>
  useQuery(['divops-adresses', query], () => getAdresseSuggestions(query), {
    enabled: query.length >= 3,
  });

export const useGeocodeAdresse = (query: string) =>
  useQuery(['divops-geocode', query], () => geocodeAdresse(query), {
    enabled: query.length >= 3,
    staleTime: 24 * 60 * 60 * 1000,
    retry: false,
  });

export const useReverseGeocodeAdresse = () =>
  useMutation({mutationFn: reverseGeocodeAdresse});

export const useGetAdressesBySiret = (siret: string) =>
  useQuery(['divops-adresses-siret', siret], () => getAdressesBySiret(siret), {
    enabled: siret.length === 14,
    retry: false,
  });

export const useCheckEligibility = () =>
  useMutation((form: EligibilityFormValues) => checkEligibility(form));

export const useCreateCommande = () =>
  useMutation((payload: CommandePayload) => createCommande(payload));

export const useGetContacts = () => useQuery(['divops-contacts'], () => getContacts());

export const useGetPortesCollecte = () =>
  useQuery(['divops-portes-collecte'], () => getPortesCollecte());

export const useGetSearchHistory = (params: DivopsHistoryQueryParams) =>
  useQuery([DIVOPS_HISTORY_QUERY_KEY, params], () => getSearchHistory(params), {
    keepPreviousData: true,
  });

export const useGetSearchHistoryFilters = () =>
  useQuery('divops-search-history-filters', () => getSearchHistoryFilters());

export const DIVOPS_APS_HISTORY_QUERY_KEY = 'divops-aps-history';

export const useGetApsRequests = (params: DivopsApsHistoryQueryParams) =>
  useQuery([DIVOPS_APS_HISTORY_QUERY_KEY, params], () => getApsRequests(params), {
    keepPreviousData: true,
  });

export const useGetApsRequestsFilters = () =>
  useQuery('divops-aps-history-filters', () => getApsRequestsFilters());

export const useSubmitApsRequest = () => {
  const queryClient = useQueryClient();
  return useMutation((payload: ApsRequestPayload) => submitApsRequest(payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(DIVOPS_APS_HISTORY_QUERY_KEY);
      queryClient.invalidateQueries('divops-aps-history-filters');
    },
  });
};

export const useSendDivopsAssistance = () =>
  useMutation((payload: DivopsAssistancePayload) => sendDivopsAssistance(payload));

export const DIVOPS_MASSE_ELIGIBILITE_QUERY_KEY = 'divops-masse-eligibilite-submissions';

export const useGetEligibiliteMasseSubmissions = () =>
  useQuery(DIVOPS_MASSE_ELIGIBILITE_QUERY_KEY, () => getEligibiliteMasseSubmissions(), {
    refetchInterval: (data?: EligibiliteMasseSubmission[]) =>
      data?.some((s) => s.statut === 'en_attente') ? 60000 : false,
  });

export const useUploadEligibiliteMasseFile = () => {
  const queryClient = useQueryClient();
  return useMutation((file: File) => uploadEligibiliteMasseFile(file), {
    onSuccess: () => queryClient.invalidateQueries(DIVOPS_MASSE_ELIGIBILITE_QUERY_KEY),
  });
};

export const useDownloadEligibiliteMasseResult = () =>
  useMutation((id: number) => downloadEligibiliteMasseResult(id));
